// Sentry for the browser bundle — client-side errors land in the SAME
// project/release as the server (brainbox, release brainbox@<git sha>), so
// "which deploy broke the UI" reads identically to the Elixir side.
//
// Config rides meta tags rendered by the root layout from the server's
// runtime config: no DSN meta (dev without SENTRY_DEV=true) means this is a
// no-op. Release-health sessions are deliberately NOT tracked here — the
// server's request-mode aggregates (Brainbox.SentrySessions) own crash-free
// numbers, and browser page-load sessions would pollute them — so the
// BrowserSession default integration is stripped. Errors only.
import * as Sentry from '@sentry/browser';

const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.content || '';

// Benign LiveView transport rejections. Hook pushes to the server are
// fire-and-forget — nothing on the client awaits the reply — so when the
// LiveSocket has dropped, the reply times out, or the view was already torn
// down/replaced, LiveView's `pushWithReply`/`pushHookEvent` rejects the promise
// and, with no `.catch()`, it becomes an `onunhandledrejection`. Every one of
// these is the user simply disconnecting or navigating away, not a bug:
//
//   - "unable to push hook event. LiveView not connected"  (view.js pushHookEvent)
//   - "timeout"                                            (view.js reply timeout)
//   - "failed with reason: {...}"  e.g. unmatched topic    (view.js reply error)
//
// A per-hook safePushEvent (see lib/safe_push.js) guards the ones we own, but it
// can't cover hooks built on branches not yet in this tree, so drop the whole
// class at the reporting boundary — the client mirror of the server's
// Brainbox.SentryFilter.
//
// Match on FUNCTION names, never filenames. `beforeSend` runs in the browser,
// where esbuild has bundled every module into a single minified `app-<digest>.js`
// — so at this point every frame's `filename` is that one bundle. The tidy
// `deps/phoenix_live_view/...` paths only exist *after* Sentry applies our
// source maps server-side, long after this filter has run. A filename-based
// check therefore never matches in prod and silently passes the whole class
// through (which is exactly how BRAINBOX-W/X escaped). Method names survive
// minification — esbuild does not rename properties — so `pushHookEvent` &c. are
// stable discriminators.
const LIVEVIEW_TRANSPORT_FNS = ['pushHookEvent', 'pushWithReply', 'pushEvent'];

// Unmistakably LiveView's own wording: no app code produces it, so the message
// alone is proof enough.
const LIVEVIEW_ONLY_MESSAGE = 'unable to push hook event. LiveView not connected';

// Generic wording that app code could plausibly also throw — only benign when it
// came out of the LiveView transport, so these keep the frame requirement.
const isGenericTransportMessage = (value) =>
  value === 'timeout' || value.startsWith('failed with reason:');

const isLiveViewTransportFrame = (frame) => {
  const fn = frame && frame.function;
  return typeof fn === 'string' && LIVEVIEW_TRANSPORT_FNS.some((n) => fn.includes(n));
};

const isBenignLiveViewRejection = (event) => {
  const values = event.exception && event.exception.values;
  if (!values || !values.length) return false;
  return values.some((ex) => {
    if (ex.mechanism && ex.mechanism.handled === true) return false;
    const value = ex.value || '';
    if (value === LIVEVIEW_ONLY_MESSAGE) return true;
    if (!isGenericTransportMessage(value)) return false;
    const frames = (ex.stacktrace && ex.stacktrace.frames) || [];
    return frames.some(isLiveViewTransportFrame);
  });
};

const dsn = meta('sentry-dsn');
if (dsn) {
  Sentry.init({
    dsn,
    release: meta('sentry-release') || undefined,
    environment: meta('sentry-environment') || undefined,
    sendClientReports: false,
    integrations: (defaults) => defaults.filter((i) => i.name !== 'BrowserSession'),
    beforeSend: (event) => (isBenignLiveViewRejection(event) ? null : event)
  });
}

// Named re-exports only, deliberately — do NOT add `export default Sentry`
// back. esbuild tree-shakes the `import * as Sentry` above down to the handful
// of entry points actually reached (init and its dependencies), but exporting
// the namespace object and importing it elsewhere forces the whole SDK to be
// retained, since property access on it can't be proven static. Measured on
// this bundle: 2.5mb with the named re-export, 3.4mb with the default.
export { addBreadcrumb } from '@sentry/browser';
